/**
 * TrustOffice Error Handling Utility
 *
 * Provides:
 * - `showError(toast, error, context)`: Extracts the best error message, auto-reports
 *   to the backend (Discord alert via /api/report-error), and shows a toast with
 *   actionable guidance.
 * - `reportErrorToBackend(error, context)`: Fire-and-forget error reporting to
 *   POST /api/report-error. Called automatically by showError, but can be used
 *   standalone for non-toast error paths.
 * - `SUPPORT_EMAIL`: Constant for the support contact.
 *
 * Usage pattern (replacing bare toast.error calls):
 *
 *   import { showError } from '../utils/errors';
 *   import { toast } from 'react-toastify';
 *
 *   // Before: toast.error('Failed to save');
 *   // After:
 *   catch (error) {
 *     showError(toast, error, { operation: 'save_trust', page: 'Onboarding' });
 *   }
 *
 * The default fallback message includes "Please try again. If the problem
 * continues, contact support@trustoffice.app." so users always have a next step.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.trustoffice.app';
const API = `${BACKEND_URL}/api`;

export const SUPPORT_EMAIL = 'support@trustoffice.app';

// Track if we've already set up the global uncaught error handler
let _globalHandlerInstalled = false;

// ---------------------------------------------------------------------------
// In-app error logging (POST /api/error-log → MongoDB, queryable via admin API)
// ---------------------------------------------------------------------------

// Debounce: don't send more than 1 error per 5 seconds from the same source
const ERROR_DEBOUNCE_MS = 5000;
const _errorLogTimestamps = new Map(); // key → last-sent timestamp

function _isDev() {
  return (
    process.env.NODE_ENV !== 'production' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  );
}

function _shouldDebounce(key) {
  const now = Date.now();
  const last = _errorLogTimestamps.get(key) || 0;
  if (now - last < ERROR_DEBOUNCE_MS) return true;
  _errorLogTimestamps.set(key, now);
  return false;
}

/**
 * Report an error to POST /api/error-log (MongoDB-backed, queryable).
 * Fire-and-forget — never throws, never blocks the UI.
 * Debounced to max 1 per 5 seconds per source key.
 * Skipped in development (NODE_ENV !== 'production' or localhost).
 *
 * @param {object} data - Error data to send
 * @param {string} debounceKey - Key for debouncing (defaults to error_type)
 */
export function reportToErrorLog(data, debounceKey = null) {
  try {
    if (_isDev()) return;

    const key = debounceKey || data.error_type || 'unknown';
    if (_shouldDebounce(key)) return;

    const payload = {
      error_type: data.error_type || 'uncaught_exception',
      error_message: data.error_message || data.message || '',
      stack_trace: data.stack || data.stack_trace || null,
      url: data.url || window.location.href,
      user_agent: data.user_agent || navigator.userAgent,
      component_stack: data.component_stack || null,
      boundary: data.boundary || false,
      metadata: data.metadata || {},
    };

    // Use fetch with keepalive so it works even during page unload
    fetch(`${API}/error-log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => { /* silent — never block on error reporting */ });
  } catch {
    // Error reporting must never throw
  }
}

/**
 * Extract the most useful error message from a thrown error or Response.
 * Handles: Response objects, Error objects, plain strings, and nested detail objects.
 */
export function extractErrorMessage(error) {
  if (!error) return 'An unexpected error occurred.';

  // If it's a Response object (from fetch)
  if (error instanceof Response) {
    return 'The server returned an error. Please try again.';
  }

  // If it has a .message (standard Error) — ensure it's a string
  if (error.message != null) {
    const msg = typeof error.message === 'string' ? error.message : JSON.stringify(error.message);
    if (msg) {
      // Check for common network errors
      if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
        return 'Network error — please check your internet connection and try again.';
      }
      return msg;
    }
  }

  // If it's a string
  if (typeof error === 'string') return error;

  // If it has a detail property (some API error wrappers)
  if (error.detail) {
    if (typeof error.detail === 'string') return error.detail;
    if (error.detail?.message) return String(error.detail.message);
    if (error.detail?.msg) return String(error.detail.msg);
    // FastAPI validation errors: array of { loc, msg, type }
    if (Array.isArray(error.detail)) {
      const msgs = error.detail
        .map(e => e?.msg || (typeof e === 'string' ? e : JSON.stringify(e)))
        .filter(Boolean);
      if (msgs.length) return msgs.join('; ');
    }
    try { return JSON.stringify(error.detail); } catch { /* fall through */ }
  }

  // FastAPI validation errors passed directly (not wrapped in { detail })
  if (Array.isArray(error)) {
    const msgs = error
      .map(e => e?.msg || (typeof e === 'string' ? e : JSON.stringify(e)))
      .filter(Boolean);
    if (msgs.length) return msgs.join('; ');
  }

  // Last resort: try to stringify, but never return [object Object]
  if (typeof error === 'object') {
    try { return JSON.stringify(error); } catch { /* fall through */ }
  }

  return 'An unexpected error occurred.';
}

/**
 * Report an error to the backend via POST /api/report-error.
 * Fire-and-forget — never throws, never blocks the UI.
 * Includes user context when available.
 */
export async function reportErrorToBackend(error, context = {}) {
  try {
    // Get user context from localStorage (best-effort)
    let userId = null;
    let email = null;
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        userId = user.id || user._id || null;
        email = user.email || null;
      }
    } catch { /* ignore */ }

    const token = localStorage.getItem('auth_token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const payload = {
      error_type: context.operation || 'frontend_error',
      message: extractErrorMessage(error),
      location: context.page || window.location.pathname,
      failing_operation: context.operation || null,
      stack: error?.stack || null,
      user_agent: navigator.userAgent,
      trust_id: context.trustId || null,
      context: {
        ...context,
        userId,
        email,
      },
      severity: context.severity || 'minor',
    };

    // Fire-and-forget — use sendBeacon if available for reliability on unloads
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon(`${API}/report-error`, blob);
    } else {
      fetch(`${API}/report-error`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => { /* silent — never block on error reporting */ });
    }
  } catch {
    // Error reporting must never throw
  }
}

/**
 * Show an error toast with actionable guidance and auto-report to backend.
 *
 * @param {object} toast - The react-toastify toast instance
 * @param {Error|string|object} error - The error that was caught
 * @param {object} context - Optional context: { operation, page, trustId, silent }
 *                           If `silent` is true, don't report to backend (just show toast)
 *
 * The toast message will always include a clear next step for the user.
 * If the error doesn't contain actionable info, the fallback is:
 * "We couldn't complete this action. Please try again, or contact support@trustoffice.app for help."
 */
export function showError(toast, error, context = {}) {
  const rawMessage = extractErrorMessage(error);

  // Build the user-facing message with a call-to-action
  let userMessage = rawMessage;

  // If the raw message doesn't already include guidance, add the default CTA
  const hasGuidance =
    userMessage.toLowerCase().includes('try again') ||
    userMessage.toLowerCase().includes('contact') ||
    userMessage.toLowerCase().includes('support') ||
    userMessage.toLowerCase().includes('subscribe') ||
    userMessage.toLowerCase().includes('upgrade') ||
    userMessage.toLowerCase().includes('sign in') ||
    userMessage.toLowerCase().includes('log in');

  if (!hasGuidance) {
    // Add actionable next step based on error type
    if (userMessage.toLowerCase().includes('network') || userMessage.toLowerCase().includes('connection')) {
      userMessage += ' Please check your internet connection and try again.';
    } else if (userMessage.toLowerCase().includes('not found') || userMessage.includes('404')) {
      userMessage += ' This may have been moved or deleted. Try refreshing the page.';
    } else if (userMessage.toLowerCase().includes('unauthorized') || userMessage.includes('401')) {
      userMessage += ' Please sign in and try again.';
    } else if (userMessage.toLowerCase().includes('forbidden') || userMessage.includes('403')) {
      userMessage += ' Your plan may not include this feature. Contact support@trustoffice.app to upgrade.';
    } else if (userMessage.toLowerCase().includes('server error') || userMessage.includes('500')) {
      userMessage += ` Our team has been notified. If this continues, contact ${SUPPORT_EMAIL}.`;
    } else {
      userMessage += ` Please try again. If the problem continues, contact ${SUPPORT_EMAIL}.`;
    }
  }

  toast.error(userMessage, { autoClose: 8000 });

  // Auto-report to backend (unless explicitly silenced)
  if (!context.silent) {
    reportErrorToBackend(error, context);
  }
}

// ---------------------------------------------------------------------------
// Global uncaught error / unhandled rejection detection
// ---------------------------------------------------------------------------

/**
 * Detect a cross-origin "Script error." — the browser's deliberately generic
 * error for a throw from a different origin (third-party script: tracker, ad,
 * widget). These carry no stack and no actionable message, so they are almost
 * always NOT our code and should not page Discord as a real bug.
 */
function _isThirdPartyScriptError(event) {
  // "Script error." is the canonical cross-origin message
  const msg = (event && (event.message || (event.error && event.error.message))) || '';
  if (!/script error/i.test(msg)) return false;
  // A real in-app error almost always carries a stack; cross-origin ones don't.
  const stack = (event.error && event.error.stack) || '';
  return !stack || stack.length < 30;
}

/**
 * Detect noise from a rejected promise that is NOT our code and should not page
 * Discord. Sources: browser extensions (Chrome/Firefox/Safari), ad-blockers,
 * password managers, and headless crawlers (Googlebot, scanners). These fire
 * real unhandledRejection events in the page but carry no actionable in-app
 * signal — paging the team on them is a false alarm every time.
 *
 * Distinct from _isThirdPartyScriptError (cross-origin "Script error."); this
 * covers unhandled promise rejections, which surface different messages and
 * never throw a cross-origin "Script error.".
 */
function _isNoiseRejection(reason) {
  const msg =
    (reason && reason.message) ||
    (typeof reason === 'string' && reason) ||
    String(reason || '');

  // Browser-extension runtime / messaging artifacts (chrome.runtime, etc.)
  if (/runtime\.sendMessage|not a registered extension|Extension context invalidated/i.test(msg)) {
    return true;
  }

  // "Error: loading script" — headless browsers / crawlers / extension content
  // scripts (Googlebot and scanner UAs produce exactly this). No in-app meaning.
  if (/^error:\s*loading script$/i.test(msg.trim())) {
    return true;
  }

  // Generic content-script / ad-blocker injection noise
  if (/content script|adblock|webRequest|loading script/i.test(msg)) {
    return true;
  }

  // Crawlers and automated scanners produce these with no user context — treat
  // bot-user-agent rejections as noise regardless of the message.
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  if (/Googlebot|bingbot|Slurp|DuckDuckBot|bot\/|crawler|spider/i.test(ua)) {
    return true;
  }

  return false;
}

/**
 * Install global uncaught error and unhandled rejection handlers.
 * Call once at app startup (e.g., in App.js or index.js).
 * These report to the backend but don't show toasts (to avoid spamming the user
 * with errors from background processes they can't see).
 */
export function installGlobalErrorHandlers() {
  if (_globalHandlerInstalled) return;
  _globalHandlerInstalled = true;

  window.addEventListener('error', (event) => {
    // Third-party cross-origin "Script error." → log to MongoDB but DO NOT
    // fire the Discord alert pipeline (it's noise, not our bug).
    const isThirdParty = _isThirdPartyScriptError(event);
    if (!isThirdParty) {
      // Report to /api/report-error (Discord alert pipeline)
      reportErrorToBackend(event.error || event.message, {
        operation: 'uncaught_exception',
        page: window.location.pathname,
      });
    }

    // MongoDB storage is handled by the backend (/api/report-error → error_logs).
    // The reportErrorToBackend call above sends to the Discord webhook pipeline,
    // which now also writes to MongoDB — one call, one pipeline.
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message = (reason && reason.message) || String(reason) || 'Unhandled promise rejection';
    const stack = (reason && reason.stack) || null;

    // Browser-extension / crawler artifacts ("Error: loading script",
    // chrome.runtime, Googlebot, scanners) are NOT our bugs. Log them to
    // MongoDB for the audit trail but DO NOT fire the Discord alert pipeline.
    const isNoise = _isNoiseRejection(reason);

    if (!isNoise) {
      // Report to /api/report-error (Discord alert pipeline)
      reportErrorToBackend(reason, {
        operation: 'unhandled_promise_rejection',
        page: window.location.pathname,
      });
    }

    // MongoDB storage is handled by the backend — reportErrorToBackend above
    // now writes to error_logs as well as sending the Discord alert.
  });
}